import gql from "graphql-tag";
import { useQuery } from "@apollo/client";
import { verifyJson } from "utils/validateAemJson";
import { globalPaths } from "@RHCommerceDev/aem/globalModels";
import { globalModels } from "@RHCommerceDev/aem/globalModels";
import { getAemEnv } from "@RHCommerceDev/aem/getAemEnv";
import {
  AemFetchHookGlobalsResult,
  AemFetchHookGlobalsVariables,
  GlobalPaths
} from "aem/fetchModel";

export const processModelResponse = (data, path = "getAemModel") => {
  const escapeXml = unsafe => {
    return unsafe.replace(/(&lt;|&gt;|&amp;|&apos;|&quot;)/g, function (c) {
      switch (c) {
        case "&lt;":
          return "<";
        case "&gt;":
          return ">";
        case "&amp;":
          return "&";
        case "&apos;":
          return "'";
        case "&quot;":
          return '"';
      }
    });
  };
  let pageContent = verifyJson(data, path) ? JSON.parse(data[path]?.model) : {};

  Object.keys(data ?? {}).forEach(
    key => {
      if (verifyJson(data, key) && pageContent) {
        pageContent[key] = JSON.parse(data[key]?.model || "");
      }
    },
    { pageContent, data }
  );
  const recursion = obj => {
    for (let i in obj) {
      if (typeof obj[i] == "object") {
        recursion(obj[i]);
      } else {
        typeof obj[i] === "string" && escapeXml(obj[i]);
      }
    }
  };
  recursion(pageContent);
  return pageContent;
};

export const globalQueryVariables = modelPath => {
  const aemEnv = getAemEnv();
  return {
    aemModelInput: {
      modelPath
    },
    ...Object.fromEntries(
      Object.entries(globalModels).map(([key, value]) => {
        return [
          key,
          {
            modelPath: globalPaths[key],
            aemEnv
          }
        ];
      })
    )
  };
};

export const queryGetAemModelGlobal = gql`
  query GetAemModel(
    $aemModelInput: AemModelInput!
    ${Object.keys(globalModels || {}).map(key => {
      return `$${key}: AemModelInput!`;
    })}
  ) {
    ${Object.keys(globalModels || {}).map(key => {
      return `${key}: getAemModel(aemModelInput: $${key}) {
        model
      }`;
    })}

    getAemModel(aemModelInput: $aemModelInput) {
      model
    }
  }
`;

const buildExtraPathVariables = (globalPaths: GlobalPaths = {}) => {
  return Object.fromEntries(
    Object.entries(globalPaths).map(([key, value]: [string, string]) => {
      return [key, { modelPath: value?.replace(/\/\/+/g, "/") }];
    })
  );
};

export const globalOnlyAemModel = (
  variables: AemFetchHookGlobalsVariables
): AemFetchHookGlobalsResult => {
  const firstPart = Object.keys(variables?.globalPaths || {}).map(key => {
    return `$${key}: AemModelInput!`;
  });

  const secondPart = Object.keys(variables?.globalPaths || {}).map(key => {
    return `${key}: getAemModel(aemModelInput: $${key}) {
      model
    }`;
  });

  const constructedQuery = `
  query GetAemModel(
    ${firstPart}
  ) {
    ${secondPart}
  }
`;
  const extraVariables = buildExtraPathVariables(variables?.globalPaths);

  const queryGetAemModelGlobal = gql`
    ${constructedQuery}
  `;

  return useQuery(queryGetAemModelGlobal, {
    variables: { ...extraVariables },
    client: variables.graphqlClient,
    skip: variables?.skip || false
  });
};

export const useGlobalAemModel = variables => {
  const firstPart = Object.keys(variables?.globalPaths || {}).map(key => {
    return `$${key}: AemModelInput!`;
  });

  const secondPart = Object.keys(variables?.globalPaths || {}).map(key => {
    return `${key}: getAemModel(aemModelInput: $${key}) {
      model
    }`;
  });

  const constructedQuery = `
  query GetAemModel(
    $aemModelInput: AemModelInput!
    ${firstPart}
  ) {
    ${secondPart}

    getAemModel(aemModelInput: $aemModelInput) {
      model
    }
  }
`;

  const queryGetAemModelGlobal = gql`
    ${constructedQuery}
  `;

  const sanitizePath = variables.modelPath.replace(/\/\/+/g, "/");

  const extraVariables = buildExtraPathVariables(variables?.globalPaths);

  return useQuery(queryGetAemModelGlobal, {
    variables: {
      aemModelInput: {
        modelPath: sanitizePath
      },
      ...extraVariables
    },
    client: variables.graphqlClient,
    skip: variables?.skip || false
  });
};

export const aemModel = variables => {
  const aemEnv = getAemEnv();
  const sanitizePath = variables.modelPath.replace(/\/\/+/g, "/");

  return useQuery(queryGetAemModelGlobal, {
    variables: {
      aemModelInput: {
        modelPath: sanitizePath,
        aemEnv
      }
    },
    client: variables.graphqlClient
  });
};

export const queryGetAemModel = gql`
  query GetAemModel($aemModelInput: AemModelInput!) {
    getAemModel(aemModelInput: $aemModelInput) {
      model
    }
  }
`;
